import React, { useEffect, useState, useRef } from 'react'
import PropTypes from 'prop-types'
import { Input, WarningText, Checkbox } from 'components/UIElements'
import {
  GROUP_TYPE_MAP,
  SCHEDULE_PLATFORM_TYPE,
  coerceSchedulePlatformType,
  isSchedulePlatformTypeValid,
} from 'utils/constants'
import contentConnect from '../containers/ContentContainer'
import CohortManagementContainer from '../../../../../../Participants/routes/CreateParticipant/containers/CohortManagementContainer'
import DeploymentSiteSelection from './DeploymentSiteSelection'

// This component is the source of truth for audience (Participants/Caregivers checkboxes). It updates
// the instrument reducer via updateCohort({ type, filter }); type is all_participants | all_caregivers
// | all_users. Save flows (instrument PUT, survey.json, etc.) read from the store so payload matches UI.

const AGE_RANGE = {
  min: 0,
  max: 100,
}

// Helper function to determine audience selection from cohort type
const getAudienceFromType = type => {
  if (type === GROUP_TYPE_MAP.allParticipants) {
    return { participants: true, caregivers: false }
  }
  if (type === GROUP_TYPE_MAP.allCaregivers) {
    return { participants: false, caregivers: true }
  }
  if (type === GROUP_TYPE_MAP.all) {
    return { participants: true, caregivers: true }
  }
  // Default: participants only (for backward compatibility with old types)
  return { participants: true, caregivers: false }
}

// Helper function to determine cohort type from audience selection
const getTypeFromAudience = (participants, caregivers) => {
  if (participants && caregivers) {
    return GROUP_TYPE_MAP.all
  }
  if (participants && !caregivers) {
    return GROUP_TYPE_MAP.allParticipants
  }
  if (!participants && caregivers) {
    return GROUP_TYPE_MAP.allCaregivers
  }
  // Default fallback
  return GROUP_TYPE_MAP.allParticipants
}

// Helper function to check if type is a filter type (age, cohort, sites)
const isFilterType = type => {
  return type === GROUP_TYPE_MAP.age || type === GROUP_TYPE_MAP.cohort || type === GROUP_TYPE_MAP.sites
}

const ParticipantManagement = props => {
  const {
    ageDisabled,
    checkedCohorts,
    checkedSites,
    cohort,
    errors,
    initializeCheckedSites,
    leafSites,
    noTracksFeature,
    sitesList,
    toggleSite,
    updateCohort,
    updateCohortField,
    updateSchedule,
    platform_type: platformType,
    isReadOnlyState,
    caregiverEnabled,
    showParticipantInstrumentPlatformTargets,
  } = props
  const getCohort = Array.isArray(cohort) ? {} : cohort
  const { filter = {}, type = '' } = getCohort

  // Determine if we're using the new audience system or old filter system
  const isNewAudienceSystem = [
    GROUP_TYPE_MAP.allParticipants,
    GROUP_TYPE_MAP.allCaregivers,
    GROUP_TYPE_MAP.all,
  ].includes(type)
  const isOldFilterSystem = isFilterType(type)

  // Initialize audience state
  // If it's a filter type (age, cohort, sites), it means participants are selected
  // If it's an audience type, use that
  // Default to participants only
  const getInitialAudience = () => {
    if (isNewAudienceSystem) {
      return getAudienceFromType(type)
    }
    if (isOldFilterSystem) {
      // Filter types implicitly mean participants are selected
      return { participants: true, caregivers: false }
    }
    // Default or empty: participants only
    return { participants: true, caregivers: false }
  }

  const [audience, setAudience] = useState(getInitialAudience())
  const hasNormalizedType = useRef(false)
  const isUserInitiatedAudienceChange = useRef(false)
  // We use a ref to track if the update is from our own action
  const isInternalUpdate = useRef(false)

  // Update audience state when type changes externally (but not when user initiates the change)
  useEffect(() => {
    // Skip if this is a user-initiated change (we already set the state in handleAudienceChange)
    if (isUserInitiatedAudienceChange.current) {
      isUserInitiatedAudienceChange.current = false
      return
    }

    if (isNewAudienceSystem) {
      setAudience(getAudienceFromType(type))
    } else if (isOldFilterSystem) {
      // Filter types mean participants are selected
      setAudience({ participants: true, caregivers: false })
      if (!hasNormalizedType.current) {
        hasNormalizedType.current = true
        isInternalUpdate.current = true
        const normalizedType = getTypeFromAudience(true, false)
        updateCohort({
          type: normalizedType,
          filter: filter,
        })
      }
    } else if (!type) {
      // Default: participants only
      setAudience({ participants: true, caregivers: false })
    }
  }, [type, caregiverEnabled])

  const handleAudienceChange = (audienceType, checked) => {
    const newAudience = {
      ...audience,
      [audienceType]: checked,
    }
    // Ensure at least one is selected
    if (!newAudience.participants && !newAudience.caregivers) {
      return
    }

    // Mark this as a user-initiated change to prevent useEffect from overriding it
    isUserInitiatedAudienceChange.current = true
    setAudience(newAudience)

    // If participants is deselected, clear filters
    if (!newAudience.participants && isOldFilterSystem) {
      // If we had filters and participants is deselected, switch to audience-only type
      const newType = getTypeFromAudience(newAudience.participants, newAudience.caregivers)
      updateCohort({
        type: newType,
        filter: {},
      })
      return
    }

    const newType = getTypeFromAudience(newAudience.participants, newAudience.caregivers)
    // If participants is selected and we have filters, preserve the filters
    // Otherwise, clear filters
    if (newAudience.participants && isOldFilterSystem) {
      // Preserve existing filters but use audience type
      updateCohort({
        type: newType,
        filter: filter, // Preserve existing filters
      })
    } else {
      // No filters, use audience type with empty filter
      // updateCohort is from ContentContainer -> schedulerActions.updateCohort (Instrument.js).
      // It dispatches UPDATE_SCHEDULE with key 'cohort'; instrument reducer sets schedule.cohort = obj.
      if (typeof updateCohort !== 'function') {
        console.error('ParticipantManagement: updateCohort is not a function', updateCohort)
      } else {
        updateCohort({
          type: newType,
          filter: {},
        })
      }
    }
  }

  // Track which filters are selected
  // Check if filters are already applied by looking at the filter object
  const getInitialSelectedFilters = (currentType, currentFilter) => {
    // Check filter object properties directly, not just the type
    // A filter is considered selected if its property exists in the filter object
    const hasAge = currentFilter.min_age !== undefined && currentFilter.max_age !== undefined
    const hasCohort = currentFilter.include !== undefined && Array.isArray(currentFilter.include)
    const hasSites = currentFilter.site_ids !== undefined && Array.isArray(currentFilter.site_ids)
    return {
      age: hasAge,
      cohort: hasCohort,
      sites: hasSites,
    }
  }

  const [selectedFilters, setSelectedFilters] = useState(() => getInitialSelectedFilters(type, filter))

  // Update selected filters state when filter changes externally (but not when we update it ourselves)
  useEffect(() => {
    if (!isInternalUpdate.current) {
      setSelectedFilters(getInitialSelectedFilters(type, filter))
    }
    isInternalUpdate.current = false
  }, [type, filter.min_age, filter.max_age, filter.include, filter.site_ids])

  const handleFilterToggle = filterType => {
    isInternalUpdate.current = true
    const newSelectedFilters = {
      ...selectedFilters,
      [filterType]: !selectedFilters[filterType],
    }
    setSelectedFilters(newSelectedFilters)

    // Build combined filter with AND logic
    // Start with empty filter and only add properties for selected filters
    const combinedFilter = {}
    const activeFilters = []
    let hasAnyFilter = false

    if (newSelectedFilters.age) {
      // Preserve existing age values or use defaults
      combinedFilter.min_age = filter.min_age !== undefined ? filter.min_age : AGE_RANGE.min
      combinedFilter.max_age = filter.max_age !== undefined ? filter.max_age : AGE_RANGE.max
      activeFilters.push('age')
      hasAnyFilter = true
    }

    if (newSelectedFilters.cohort) {
      // Preserve existing cohort selections or use current checkedCohorts
      const cohortIds =
        Object.keys(checkedCohorts).length > 0
          ? Object.keys(checkedCohorts).map(id => parseInt(id, 10))
          : filter.include && Array.isArray(filter.include) && filter.include.length > 0
          ? filter.include
          : []
      // Only add include if we have cohort IDs
      if (cohortIds.length > 0) {
        combinedFilter.include = cohortIds
        activeFilters.push('cohort')
        hasAnyFilter = true
      }
    }

    if (newSelectedFilters.sites) {
      // Preserve existing site selections or use current checkedSites
      const siteIds =
        Object.keys(checkedSites).length > 0
          ? Object.keys(checkedSites).map(id => parseInt(id, 10))
          : filter.site_ids && Array.isArray(filter.site_ids) && filter.site_ids.length > 0
          ? filter.site_ids
          : []
      // Only add site_ids if we have site IDs
      if (siteIds.length > 0) {
        combinedFilter.site_ids = siteIds
        activeFilters.push('sites')
        hasAnyFilter = true
      }
    }

    const newType = getTypeFromAudience(audience.participants, audience.caregivers)

    if (!hasAnyFilter || activeFilters.length === 0) {
      // No filters, use audience type with empty filter
      updateCohort({
        type: newType,
        filter: {},
      })
    } else {
      // Filters are selected - use audience type with combined filter properties
      // All filter properties are combined in the filter object with AND logic
      updateCohort({
        type: newType,
        filter: combinedFilter,
      })
    }
  }

  // Wrapper for updateCohortField that preserves all selected filter properties
  const updateCohortFieldWithPreservation = (field, value, errorKey) => {
    isInternalUpdate.current = true

    // Build the new filter object preserving all selected filter properties
    const newFilter = { ...filter }

    // Update the specific field
    if (field === 'filter') {
      // If updating the entire filter, merge with existing values
      // This preserves all existing filter properties
      Object.assign(newFilter, value)
    } else {
      // If updating a nested field, preserve all other filter properties
      newFilter[field] = value
    }

    // Ensure all selected filters are preserved in the new filter object
    // This is critical when one filter's update might overwrite others
    if (selectedFilters.age) {
      // Preserve age filter if it's selected
      if (newFilter.min_age === undefined && newFilter.max_age === undefined) {
        // Only set defaults if both are missing (not just updating one)
        newFilter.min_age = filter.min_age !== undefined ? filter.min_age : AGE_RANGE.min
        newFilter.max_age = filter.max_age !== undefined ? filter.max_age : AGE_RANGE.max
      } else {
        // Preserve existing values if they exist
        if (newFilter.min_age === undefined && filter.min_age !== undefined) {
          newFilter.min_age = filter.min_age
        }
        if (newFilter.max_age === undefined && filter.max_age !== undefined) {
          newFilter.max_age = filter.max_age
        }
      }
    }

    if (selectedFilters.cohort) {
      // Preserve cohort filter if it's selected
      if (!newFilter.include || !Array.isArray(newFilter.include)) {
        const cohortIds =
          Object.keys(checkedCohorts).length > 0
            ? Object.keys(checkedCohorts).map(id => parseInt(id, 10))
            : filter.include && Array.isArray(filter.include)
            ? filter.include
            : []
        newFilter.include = cohortIds
      }
    }

    if (selectedFilters.sites) {
      // Preserve sites filter if it's selected
      if (!newFilter.site_ids || !Array.isArray(newFilter.site_ids)) {
        const siteIds =
          Object.keys(checkedSites).length > 0
            ? Object.keys(checkedSites).map(id => parseInt(id, 10))
            : filter.site_ids && Array.isArray(filter.site_ids)
            ? filter.site_ids
            : []
        newFilter.site_ids = siteIds
      }
    }

    // Update the cohort with the preserved filter; type comes only from audience selection
    const currentCohortType = getTypeFromAudience(audience.participants, audience.caregivers)

    // Use updateCohort to update both type and filter to ensure consistency
    updateCohort({
      type: currentCohortType,
      filter: newFilter,
    })
  }

  const filtersDisabled = !(audience.participants || audience.caregivers) || isReadOnlyState

  // Legacy instruments may have no schedule.platform_type in Redux while checkboxes derive from coercion.
  // Persist the coerced default once so validation and save match what the user sees.
  useEffect(() => {
    if (!showParticipantInstrumentPlatformTargets || isReadOnlyState) return
    if (isSchedulePlatformTypeValid(platformType)) return
    updateSchedule('platform_type', coerceSchedulePlatformType(platformType))
  }, [showParticipantInstrumentPlatformTargets, isReadOnlyState, platformType, updateSchedule])

  const coercedPlatformType = coerceSchedulePlatformType(platformType)
  const platformSelection = {
    mobile: coercedPlatformType === SCHEDULE_PLATFORM_TYPE.mobile || coercedPlatformType === SCHEDULE_PLATFORM_TYPE.all,
    web: coercedPlatformType === SCHEDULE_PLATFORM_TYPE.web || coercedPlatformType === SCHEDULE_PLATFORM_TYPE.all,
  }

  const handlePlatformChange = (platformKey, checked) => {
    const nextMobile = platformKey === 'mobile' ? checked : platformSelection.mobile
    const nextWeb = platformKey === 'web' ? checked : platformSelection.web
    if (!nextMobile && !nextWeb) return
    let nextType
    if (nextMobile && nextWeb) nextType = SCHEDULE_PLATFORM_TYPE.all
    else if (nextMobile) nextType = SCHEDULE_PLATFORM_TYPE.mobile
    else nextType = SCHEDULE_PLATFORM_TYPE.web
    updateSchedule('platform_type', nextType)
  }

  return (
    <div className='ptp-management'>
      <p>
        Who should receive this instrument?
        {errors['schedule.cohort.filter.error'] && (
          <WarningText text={errors['schedule.cohort.filter.error']} disabled />
        )}
      </p>

      {/* Audience Selection */}
      <div style={{ marginBottom: '20px' }}>
        <p style={{ fontWeight: 400, marginBottom: '8px' }}>Audience (required)</p>
        <p style={{ fontSize: '12px', color: '#666', marginBottom: '12px' }}>Select all that apply:</p>
        <Checkbox
          id='audience-participants'
          checked={audience.participants}
          disabled={isReadOnlyState}
          onClick={() => handleAudienceChange('participants', !audience.participants)}
          label='Participants'
        />
        <Checkbox
          id='audience-caregivers'
          checked={audience.caregivers}
          disabled={isReadOnlyState}
          onClick={() => handleAudienceChange('caregivers', !audience.caregivers)}
          label='Caregivers'
        />
      </div>

      {/* Filters Section */}
      {(audience.participants || audience.caregivers) && (
        <div style={{ marginBottom: '20px' }}>
          <p style={{ fontWeight: 400, marginBottom: '8px' }}>Filters (optional)</p>
          <p style={{ fontSize: '12px', color: '#666', marginBottom: '12px' }}>
            Filters applied to participants control which participants and their caregivers receive the instrument.
          </p>
          {!ageDisabled && (
            <div style={{ marginBottom: '8px' }}>
              <Checkbox
                id='filter-age-range'
                checked={selectedFilters.age}
                disabled={filtersDisabled}
                onClick={() => handleFilterToggle('age')}
                label='Age Range'
              />
              {selectedFilters.age && (
                <div style={{ marginLeft: '24px', marginTop: '4px' }}>
                  <AgeRangeInput
                    {...filter}
                    updateCohortField={updateCohortFieldWithPreservation}
                    errors={errors}
                    type={GROUP_TYPE_MAP.age}
                    isReadOnlyState={isReadOnlyState}
                  />
                </div>
              )}
            </div>
          )}
          {!noTracksFeature && (
            <div style={{ marginBottom: '8px' }}>
              <Checkbox
                id='filter-track'
                checked={selectedFilters.cohort}
                disabled={filtersDisabled}
                onClick={() => handleFilterToggle('cohort')}
                label='Track'
              />
              {selectedFilters.cohort && (
                <div style={{ marginLeft: '24px', marginTop: '4px' }}>
                  <CohortInput
                    checkedCohorts={checkedCohorts}
                    errorText={errors['schedule.cohort.filter.include']}
                    hasError={errors.hasOwnProperty('schedule.cohort.filter.include')}
                    type={GROUP_TYPE_MAP.cohort}
                    updateCohortField={updateCohortFieldWithPreservation}
                    isReadOnlyState={isReadOnlyState}
                  />
                </div>
              )}
            </div>
          )}
          <div style={{ marginBottom: '8px' }}>
            <Checkbox
              id='filter-site'
              checked={selectedFilters.sites}
              disabled={filtersDisabled}
              onClick={() => handleFilterToggle('sites')}
              label='Site'
            />
            {selectedFilters.sites && (
              <div style={{ marginLeft: '24px', marginTop: '4px' }}>
                <SiteInput
                  checkedSites={checkedSites}
                  errors={errors}
                  hasError={errors.hasOwnProperty('schedule.cohort.filter.site_ids')}
                  initializeCheckedSites={initializeCheckedSites}
                  leafSites={leafSites}
                  siteIds={filter?.site_ids}
                  sitesList={sitesList}
                  toggleSite={toggleSite}
                  type={GROUP_TYPE_MAP.sites}
                  updateCohortField={updateCohortFieldWithPreservation}
                  isReadOnlyState={isReadOnlyState}
                />
              </div>
            )}
          </div>
        </div>
      )}

      {showParticipantInstrumentPlatformTargets ? (
        <div style={{ marginBottom: '20px' }}>
          <p style={{ fontWeight: 400, marginBottom: '8px' }}>Platform (required)</p>
          <p style={{ fontSize: '12px', color: '#666', marginBottom: '12px' }}>Select all that apply:</p>
          {errors['schedule.platform_type'] && <WarningText text={errors['schedule.platform_type']} disabled />}
          <Checkbox
            id='platform-mobile'
            checked={platformSelection.mobile}
            disabled={isReadOnlyState}
            onClick={() => handlePlatformChange('mobile', !platformSelection.mobile)}
            label='Mobile'
          />
          <Checkbox
            id='platform-web'
            checked={platformSelection.web}
            disabled={isReadOnlyState}
            onClick={() => handlePlatformChange('web', !platformSelection.web)}
            label='Web'
          />
        </div>
      ) : null}
    </div>
  )
}

const AgeRangeInput = ({ min_age, max_age, updateCohortField, errors, type, isReadOnlyState }) => {
  const minAge = min_age || min_age === AGE_RANGE.min ? min_age : ''
  const maxAge = max_age || max_age === AGE_RANGE.min ? max_age : ''
  const hasError = !!(errors['schedule.cohort.filter.min_age'] || errors['schedule.cohort.filter.min_age'])
  const errorText = 'Minimum and maximum age must be set'
  const onUpdateCohortField = field => value => {
    const otherField = field === 'max_age' ? 'min_age' : 'max_age'
    const otherValue = field === 'max_age' ? min_age : max_age
    if (value < AGE_RANGE.min || value > AGE_RANGE.max) return
    updateCohortField(
      'filter',
      { [field]: parseInt(value, 10), [otherField]: parseInt(otherValue, 10) },
      `schedule.cohort.filter${field}`,
    )
  }

  return (
    <span>
      Age Range
      {type === GROUP_TYPE_MAP.age && (
        <>
          <Input
            hasError={errors.hasOwnProperty('schedule.cohort.filter.min_age')}
            onBlur={() => {
              if (min_age > max_age) {
                onUpdateCohortField('max_age')(min_age)
              }
            }}
            className='age-input'
            id='min-age'
            placeholder='Min Age'
            type='number'
            value={minAge}
            onChange={onUpdateCohortField('min_age')}
            disabled={isReadOnlyState}
          />
          <span>to</span>
          <Input
            hasError={errors.hasOwnProperty('schedule.cohort.filter.max_age')}
            onBlur={() => {
              if (max_age < min_age) {
                onUpdateCohortField('min_age')(max_age)
              }
            }}
            className='age-input'
            id='max-age'
            placeholder='Max Age'
            type='number'
            value={maxAge}
            onChange={onUpdateCohortField('max_age')}
            max={AGE_RANGE.max}
            disabled={isReadOnlyState}
          />
          {hasError && <WarningText text={errorText} />}
        </>
      )}
    </span>
  )
}

export const CohortInput = ({ checkedCohorts, errorText, updateCohortField, hasError, type, isReadOnlyState }) => {
  return (
    <div className='cohort-radio-column'>
      {type === GROUP_TYPE_MAP.cohort && (
        <CohortManagementContainer
          className={`cohort-management-compact${hasError ? ' has-error' : ''}`}
          errorText={errorText}
          hasError={hasError}
          onToggleCohort={id => {
            const newCohortData = { ...checkedCohorts }
            if (id in checkedCohorts) {
              delete newCohortData[id]
            } else {
              newCohortData[id] = null
            }
            updateCohortField(
              'filter',
              { include: Object.keys(newCohortData).map(_id => parseInt(_id, 10)) },
              'schedule.cohort.filter.include',
            )
          }}
          isReadOnlyState={isReadOnlyState}
        />
      )}
    </div>
  )
}

export const SiteInput = ({
  checkedSites,
  errors,
  hasError,
  initializeCheckedSites,
  leafSites,
  siteIds,
  sitesList,
  toggleSite,
  type,
  updateCohortField,
  isReadOnlyState,
}) => {
  const errorText = errors['schedule.cohort.filter.site_ids']
  return (
    <>
      {type === GROUP_TYPE_MAP.sites && (
        <DeploymentSiteSelection
          checkedSites={checkedSites}
          errorText={errorText}
          hasError={hasError}
          leafSites={leafSites}
          siteIds={siteIds}
          sitesList={sitesList}
          toggleSite={toggleSite}
          updateCohortField={updateCohortField}
          initializeCheckedSites={initializeCheckedSites}
          isReadOnlyState={isReadOnlyState}
        />
      )}
    </>
  )
}

AgeRangeInput.propTypes = {
  errors: PropTypes.object,
  max_age: PropTypes.number,
  min_age: PropTypes.number,
  type: PropTypes.string,
  updateCohortField: PropTypes.func,
}

CohortInput.propTypes = {
  checkedCohorts: PropTypes.objectOf(PropTypes.number),
  errorText: PropTypes.string,
  hasError: PropTypes.bool,
  type: PropTypes.string,
  updateCohortField: PropTypes.func,
  isReadOnlyState: PropTypes.bool,
}

SiteInput.propTypes = {
  checkedSites: PropTypes.objectOf(PropTypes.string),
  errors: PropTypes.object,
  hasError: PropTypes.bool,
  initializeCheckedSites: PropTypes.func,
  leafSites: PropTypes.arrayOf(PropTypes.number),
  siteIds: PropTypes.arrayOf(PropTypes.number),
  sitesList: PropTypes.arrayOf(PropTypes.array),
  toggleSite: PropTypes.func,
  type: PropTypes.string,
  updateCohortField: PropTypes.func,
  isReadOnlyState: PropTypes.bool,
}

ParticipantManagement.propTypes = {
  ageDisabled: PropTypes.bool,
  checkedCohorts: PropTypes.objectOf(PropTypes.number),
  checkedSites: PropTypes.objectOf(PropTypes.string),
  cohort: PropTypes.oneOfType([PropTypes.object, PropTypes.arrayOf(PropTypes.object)]),
  errors: PropTypes.object,
  leafSites: PropTypes.arrayOf(PropTypes.number),
  noTracksFeature: PropTypes.bool,
  initializeCheckedSites: PropTypes.func,
  sitesList: PropTypes.arrayOf(PropTypes.array),
  toggleSite: PropTypes.func,
  updateCohort: PropTypes.func,
  updateCohortField: PropTypes.func,
  updateSchedule: PropTypes.func,
  platform_type: PropTypes.oneOfType([
    PropTypes.oneOf([SCHEDULE_PLATFORM_TYPE.mobile, SCHEDULE_PLATFORM_TYPE.web, SCHEDULE_PLATFORM_TYPE.all]),
    PropTypes.shape({ mobile: PropTypes.bool, web: PropTypes.bool }),
  ]),
  showParticipantInstrumentPlatformTargets: PropTypes.bool,
  caregiverEnabled: PropTypes.bool,
}

export default contentConnect(ParticipantManagement)
